/* Print Documents — tick invoices + Packing List / Bulk Data, choose copies, print all in one go. */
import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Eye, FileDown, FileSpreadsheet, Loader2, Printer, Table2 } from "lucide-react";

const PAGE_W = { land: 1060, port: 730 };
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const CSS = `@page land{size:A4 landscape;margin:8mm}@page port{size:A4 portrait;margin:8mm}
*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{margin:0;font-family:Calibri,'Segoe UI',Arial,sans-serif;color:#0f172a;counter-reset:pg}
.pg{break-after:page;counter-increment:pg;padding:2px}.pg:last-child{break-after:auto}.pg.land{page:land}.pg.port{page:port}
.hd{display:flex;align-items:center;gap:10px;border-bottom:3px solid #1e3a8a;padding:0 0 4px;margin-bottom:6px;font-size:12px}
.tag{color:#fff;font-weight:800;border-radius:4px;padding:2px 8px;font-size:12px}.tag.packing_list{background:#1d4ed8}.tag.bulk_data{background:#047857}
.inv{font-family:Consolas,monospace;font-size:15px;font-weight:800}.fn{color:#64748b;font-size:10px;flex:1;overflow:hidden;white-space:nowrap;text-overflow:ellipsis}
.cp{background:#fef3c7;color:#92400e;border-radius:999px;padding:1px 8px;font-weight:700;font-size:10px}
.pn:after{content:"Page " counter(pg);font-size:10px;color:#64748b}
table.xl{border-collapse:collapse;table-layout:fixed;font-size:11px}table.xl td{padding:1px 3px;vertical-align:top;overflow:hidden;word-wrap:break-word}
@media screen{body{background:#e2e8f0;padding:12px}.pg{background:#fff;margin:0 auto 14px;box-shadow:0 2px 10px #0003;padding:14px;width:fit-content}}`;

const buildHtml = (docs, copies, fit = true) => {
  const pages = [];
  docs.forEach(d => d.sheets.forEach(s => {
    const o = s.landscape ? "land" : "port";
    const zoom = fit ? Math.min(1, PAGE_W[o] / Math.max(1, s.width)).toFixed(3) : 1;
    for (let c = 1; c <= copies; c++) {
      pages.push(`<section class="pg ${o}"><div class="hd"><span class="tag ${d.type}">${esc(d.label)}</span><span class="inv">${esc(d.invoice_no)}</span>
        <span class="fn">${esc(d.filename)}${d.sheets.length > 1 ? ` · ${esc(s.name)}` : ""}</span>${copies > 1 ? `<span class="cp">Copy ${c}/${copies}</span>` : ""}<span class="pn"></span></div>
        <div style="zoom:${zoom}">${s.html}</div></section>`);
    }
  }));
  return `<html><head><title>Documents ${esc(docs.map(d => d.invoice_no).filter((v, i, a) => a.indexOf(v) === i).join(", "))}</title><style>${CSS}</style></head><body>${pages.join("")}</body></html>`;
};

export const useDocSelection = () => {
  const [sel, setSel] = useState(() => new Set());
  const toggle = (k) => setSel(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  return { sel, setSel, toggle };
};

export const SelectBox = ({ k, sel, toggle, testid }) => (
  <input type="checkbox" checked={sel.has(k)} onChange={() => toggle(k)} onClick={e => e.stopPropagation()}
    className="w-4 h-4 accent-indigo-600 cursor-pointer" data-testid={testid} />
);

const TypeChip = ({ on, onClick, icon: Icon, label, tone, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${on ? tone : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"}`}>
    <Icon className="w-3.5 h-3.5" />{label}
  </button>
);

const saveBlob = (r, fallback) => {
  const cd = r.headers["content-disposition"] || "";
  const name = (cd.match(/filename="?([^"]+)"?/) || [])[1] || fallback;
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  const miss = r.headers["x-missing-docs"];
  if (miss) toast.warning(`Not found: ${miss}`, { duration: 8000 });
};

const blobError = async (e, msg) => {
  try { toast.error(JSON.parse(await e.response.data.text()).detail || msg); } catch { toast.error(msg); }
};

const OutBtn = ({ onClick, busy, disabled, icon: Icon, label, testid }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-300 bg-white hover:bg-indigo-50 disabled:opacity-50 text-indigo-700 px-3 py-1.5 text-xs font-bold transition-colors">
    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}{label}
  </button>
);

// Toolbar: select all, doc types, copies, Print selected.
export const DocPrintBar = ({ keys, sel, setSel, testid = "docprint", label = "Select all" }) => {
  const [types, setTypes] = useState({ packing_list: true, bulk_data: true });
  const [copies, setCopies] = useState(1);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState("");
  const [preview, setPreview] = useState(null);
  const frame = useRef(null);
  const all = keys.length > 0 && keys.every(k => sel.has(k));
  const picked = keys.filter(k => sel.has(k));
  const tlist = Object.keys(types).filter(t => types[t]);
  const go = async () => {
    if (!picked.length || !tlist.length) return toast.error("Tick at least one invoice and one document");
    const w = window.open("", "_blank");
    if (!w) return toast.error("Allow pop-ups to print");
    w.document.write("<p style='font-family:sans-serif;padding:20px'>Preparing documents…</p>");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/packing-list/print-docs`, { invoices: picked, types: tlist }, { headers: getAuthHeader() });
      const { docs, missing } = r.data;
      if (missing.length) toast.warning(`Not found: ${missing.map(m => `${m.invoice_no} ${m.label}`).join(", ")}`, { duration: 8000 });
      if (!docs.length) { w.close(); return; }
      w.document.open(); w.document.write(buildHtml(docs, copies)); w.document.close();
      setTimeout(() => w.print(), 500);
    } catch (e) {
      w.close();
      toast.error(e.response?.data?.detail || "Could not prepare documents");
    } finally { setBusy(false); }
  };
  const download = async (kind) => {
    if (!picked.length || !tlist.length) return toast.error("Tick at least one invoice and one document");
    setOut(kind);
    try {
      const r = await axios.post(`${API}/packing-list/print-docs/${kind}`, { invoices: picked, types: tlist, copies },
        { headers: getAuthHeader(), responseType: "blob" });
      saveBlob(r, kind === "pdf" ? "Documents.pdf" : "Documents.zip");
    } catch (e) { await blobError(e, `Could not build ${kind.toUpperCase()}`); } finally { setOut(""); }
  };
  const view = async () => {
    if (!picked.length || !tlist.length) return toast.error("Tick at least one invoice and one document");
    setOut("view");
    try {
      const r = await axios.post(`${API}/packing-list/print-docs`, { invoices: picked, types: tlist }, { headers: getAuthHeader() });
      if (r.data.missing.length) toast.warning(`Not found: ${r.data.missing.map(m => `${m.invoice_no} ${m.label}`).join(", ")}`, { duration: 8000 });
      if (r.data.docs.length) setPreview(buildHtml(r.data.docs, 1, false));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not prepare documents"); } finally { setOut(""); }
  };
  const off = !!out || busy || !picked.length || !tlist.length;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-white px-3 py-2" data-testid={testid}>
      <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 cursor-pointer">
        <input type="checkbox" checked={all} onChange={() => setSel(all ? new Set() : new Set(keys))}
          className="w-4 h-4 accent-indigo-600" data-testid={`${testid}-select-all`} />
        {label} <span className="text-slate-400 font-normal">({picked.length}/{keys.length})</span>
      </label>
      <span className="w-px h-5 bg-slate-200 mx-1" />
      <span className="text-[11px] font-semibold text-slate-500">Print:</span>
      <TypeChip on={types.packing_list} onClick={() => setTypes(t => ({ ...t, packing_list: !t.packing_list }))} icon={Table2}
        label="Packing List" tone="bg-blue-600 text-white border-blue-600" testid={`${testid}-type-packing_list`} />
      <TypeChip on={types.bulk_data} onClick={() => setTypes(t => ({ ...t, bulk_data: !t.bulk_data }))} icon={FileSpreadsheet}
        label="Bulk Data" tone="bg-emerald-600 text-white border-emerald-600" testid={`${testid}-type-bulk_data`} />
      <label className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 ml-1">Copies
        <select value={copies} onChange={e => setCopies(Number(e.target.value))} data-testid={`${testid}-copies`}
          className="h-7 rounded-md border border-slate-300 bg-white px-1 text-xs font-semibold text-slate-800">
          {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <span className="ml-auto" />
      <OutBtn onClick={view} busy={out === "view"} disabled={off} icon={Eye} label="Screen view" testid={`${testid}-view`} />
      <OutBtn onClick={() => download("excel")} busy={out === "excel"} disabled={off} icon={FileSpreadsheet} label="Excel" testid={`${testid}-excel`} />
      <OutBtn onClick={() => download("pdf")} busy={out === "pdf"} disabled={off} icon={FileDown} label="PDF" testid={`${testid}-pdf`} />
      <button type="button" onClick={go} disabled={busy || !picked.length || !tlist.length} data-testid={`${testid}-print`}
        className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-1.5 text-xs font-bold shadow-sm transition-colors">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
        Print selected{picked.length ? ` (${picked.length} invoice${picked.length > 1 ? "s" : ""} × ${tlist.length} doc${tlist.length > 1 ? "s" : ""}${copies > 1 ? ` × ${copies}` : ""})` : ""}
      </button>
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-6xl w-[95vw] p-4" data-testid={`${testid}-preview`}>
          <DialogHeader className="flex-row items-center justify-between space-y-0 pr-8">
            <DialogTitle className="text-sm">Screen view — {picked.length} invoice{picked.length > 1 ? "s" : ""}</DialogTitle>
            <button type="button" onClick={() => frame.current?.contentWindow?.print()} data-testid={`${testid}-preview-print`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 text-xs font-semibold transition-colors">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
          </DialogHeader>
          {preview && <iframe ref={frame} title="Documents" srcDoc={preview} className="w-full h-[75vh] rounded-md border border-slate-200" data-testid={`${testid}-preview-frame`} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
